import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';

const LanguageContext = createContext(null);

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const translations = {
  fr: {
    // Landing
    heroTitle: "Et si tu vivais enfin la vie que tu mérites ?",
    heroSubtitle: "L'IA analyse tes réponses et te révèle ta vocation unique. Le métier qui te correspond vraiment. Le chemin pour y arriver.",
    startJourney: "Découvrir ma vocation",
    login: "Connexion",
    register: "Inscription",
    
    // Auth
    email: "Email",
    password: "Mot de passe",
    name: "Prénom",
    confirmPassword: "Confirmer le mot de passe",
    noAccount: "Pas encore de compte ?",
    hasAccount: "Déjà un compte ?",
    createAccount: "Créer un compte",
    
    // Dashboard
    welcome: "Bienvenue",
    yourJourney: "Ton parcours Ikigai",
    progress: "Progression",
    level: "Niveau",
    modulesCompleted: "modules complétés",
    startModule: "Commencer",
    continueModule: "Continuer",
    completed: "Complété",
    viewResults: "Voir mes résultats",
    generateAnalysis: "Générer mon profil Ikigai",
    
    // Modules
    questionsLeft: "questions restantes",
    nextQuestion: "Question suivante",
    previousQuestion: "Question précédente",
    saveAndFinish: "Terminer le module",
    answerPlaceholder: "Prends le temps de réfléchir...",
    
    // Results
    yourIkigai: "Ton Ikigai",
    personalityProfile: "Profil de personnalité",
    passions: "Tes passions",
    talents: "Tes talents",
    causes: "Tes causes",
    idealEnvironments: "Environnements idéaux",
    careerPaths: "Pistes de carrière",
    concreteProjects: "Projets concrets à tester",
    ikigaiSummary: "En résumé",
    
    // Misc
    logout: "Déconnexion",
    settings: "Paramètres",
    language: "Langue",
    french: "Français",
    english: "English",
    loading: "Chargement...",
    error: "Une erreur est survenue",
    retry: "Réessayer"
  },
  en: {
    // Landing
    heroTitle: "What if you finally lived the life you deserve?",
    heroSubtitle: "AI analyzes your answers and reveals your unique calling. The career that truly fits you. The path to get there.",
    startJourney: "Discover my calling",
    login: "Login",
    register: "Sign up",
    
    // Auth
    email: "Email",
    password: "Password",
    name: "First name",
    confirmPassword: "Confirm password",
    noAccount: "Don't have an account?",
    hasAccount: "Already have an account?",
    createAccount: "Create account",
    
    // Dashboard
    welcome: "Welcome",
    yourJourney: "Your Ikigai Journey",
    progress: "Progress",
    level: "Level",
    modulesCompleted: "modules completed",
    startModule: "Start",
    continueModule: "Continue",
    completed: "Completed",
    viewResults: "View my results",
    generateAnalysis: "Generate my Ikigai profile",
    
    // Modules
    questionsLeft: "questions left",
    nextQuestion: "Next question",
    previousQuestion: "Previous question",
    saveAndFinish: "Finish module",
    answerPlaceholder: "Take time to reflect...",
    
    // Results
    yourIkigai: "Your Ikigai",
    personalityProfile: "Personality profile",
    passions: "Your passions",
    talents: "Your talents",
    causes: "Your causes",
    idealEnvironments: "Ideal environments",
    careerPaths: "Career paths",
    concreteProjects: "Concrete projects to try",
    ikigaiSummary: "In summary",
    
    // Misc
    logout: "Logout",
    settings: "Settings",
    language: "Language",
    french: "Français",
    english: "English",
    loading: "Loading...",
    error: "An error occurred",
    retry: "Retry"
  }
};

export const LanguageProvider = ({ children }) => {
  const { user } = useAuth();
  const [language, setLanguage] = useState(user?.language || 'fr');

  useEffect(() => {
    if (user?.language) {
      setLanguage(user.language);
    }
  }, [user?.language]);

  const t = (key) => {
    return translations[language]?.[key] || translations['fr'][key] || key;
  };

  const changeLanguage = useCallback(async (newLanguage) => {
    setLanguage(newLanguage);
    if (user) {
      try {
        await axios.put(`${API}/user/language?language=${newLanguage}`);
      } catch (e) { console.warn('Language update:', e.message); }
    }
  }, [user]);

  return (
    <LanguageContext.Provider value={{ language, setLanguage: changeLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within LanguageProvider');
  }
  return context;
};
